// Eager, and first — App/OldApp below are lazy, so importing this from them would delay the
// @font-face registration and woff2 preload until that chunk parses. `font-display: optional` only
// gives the font a ~100ms block window, so a late registration means the fallback gets locked in for
// the whole page load. Registering from the entry chunk starts the fetch as early as we can from JS.
import './fonts/outfitVariable';

import { lazy, StrictMode, Suspense } from 'react';

import { createRoot } from 'react-dom/client';

import { AnalyticsProvider } from './analytics/provider.tsx';
import { ErrorBoundary } from './components/ErrorBoundary';
import { isWarmBoot } from './modules/warm/warmBoot';
import { initRiveRuntime } from './rive/initRiveRuntime.ts';

import './index.css';
import './index.scss';

initRiveRuntime();

const OldApp = lazy(() => import('./app-old/App.tsx'));
const App = lazy(() => import('./App.tsx'));

const { VITE_GOOGLE_ANALYTICS_MEASUREMENT_ID, VITE_SEGMENT_APP_KEY } = import.meta.env;

const searchParams = new URLSearchParams(window.location.search);
const isNewGameUi = Number(searchParams.get('version')) > 1;

// A warm document exists precisely so a later launch is cheap, and it is idle by definition — so spend
// that idle time pulling the metagame route chunks it will need. Without this, warm removes the boot but
// the click still waits on a chunk download, which is most of what is left.
if (isWarmBoot()) {
  // Imported dynamically, and only here. Importing these statically pulled lazy-routes and
  // lazy-metagames into the ENTRY chunk, which reshaped the chunk graph for every boot — including
  // slots, which need neither. The slot route is wrapped in a lazy PrizeDropProviderWrapper under a
  // Suspense with an empty fallback, so anything that makes that tree suspend renders a blank screen.
  void import('./router/lazy-routes').then(({ prefetchJackpot, prefetchPrizeDrop, prefetchSports }) => {
    prefetchPrizeDrop();
    prefetchSports();
    prefetchJackpot();
  });
  void import('./modules/metagames/lazy-metagames').then(({ prefetchMetagame }) => {
    for (const game of ['trail', 'hammers', 'puzzle'] as const) {
      prefetchMetagame(game);
    }
  });
}

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <ErrorBoundary>
      <AnalyticsProvider
        applicationName="game-ui"
        googleAnalyticsMeasurementIds={[VITE_GOOGLE_ANALYTICS_MEASUREMENT_ID]}
        segmentAppKey={VITE_SEGMENT_APP_KEY}
      >
        <Suspense fallback={<></>}>{isNewGameUi ? <App /> : <OldApp />}</Suspense>
      </AnalyticsProvider>
    </ErrorBoundary>
  </StrictMode>,
);
